import { motion } from "framer-motion";
import { useState } from "react";
import { Mail, Phone, MapPin, Send, Check } from "lucide-react";
import { toast } from "sonner";

const FORMSPREE_ID = "mlgzzlpb";

const INTEREST_OPTIONS = [
  "Partnership / Collaboration",
  "Research & Translational Support",
  "MedTech / Software Solutions",
  "Pipeline / Clinical Inquiry",
  "Investor Relations",
  "Media / Press",
  "Careers",
  "Other",
];

const FloatingInput = ({ id, label, type = "text", value, onChange, required, testid }) => {
  const [focus, setFocus] = useState(false);
  const active = focus || !!value;
  return (
    <div className="relative">
      <input id={id} type={type} value={value} onChange={onChange} onFocus={() => setFocus(true)} onBlur={() => setFocus(false)} required={required} data-testid={testid} className="peer w-full bg-transparent border-b border-white/15 px-1 pt-7 pb-2.5 text-white outline-none focus:border-[#FF2400] transition-colors" autoComplete="off" />
      <label htmlFor={id} className={"absolute left-1 transition-all duration-200 pointer-events-none " + (active ? "top-1 text-[10px] tracking-[0.25em] text-[#FF6A4D] uppercase" : "top-7 text-base text-white/40")}>{label}</label>
    </div>
  );
};

export const Contact = () => {
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", organisation: "", area_of_interest: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email || !form.message) { toast.error("Please fill all required fields."); return; }
    setSubmitting(true);
    try {
      const response = await fetch("https://formspree.io/f/" + FORMSPREE_ID, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({ name: form.first_name + " " + form.last_name, email: form.email, organisation: form.organisation, area_of_interest: form.area_of_interest, message: form.message }),
      });
      if (response.ok) { setDone(true); toast.success("Thank you. We will be in touch."); setForm({ first_name: "", last_name: "", email: "", organisation: "", area_of_interest: "", message: "" }); }
      else { throw new Error("Submission failed"); }
    } catch (err) { console.error(err); toast.error("Something went wrong. Please try again."); }
    finally { setSubmitting(false); }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative section-pad bg-[#0A0A0C] overflow-hidden">
      <div className="absolute inset-x-0 top-0 divider-grad" />
      <div className="absolute inset-0 grid-bg opacity-[0.06] pointer-events-none" />
      <div className="absolute top-40 -right-40 w-[520px] h-[520px] bg-radial-red opacity-70 blur-3xl pointer-events-none" />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-2 h-2 rounded-full bg-[#E60000]" />
          <span className="eyebrow">GET IN TOUCH</span>
        </div>
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <motion.h2 initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter font-medium text-white">
              Let us build the<br /><span className="italic font-light">response.</span>
            </motion.h2>
            <p className="mt-6 text-blude-textDim text-base max-w-md leading-relaxed text-justify">For partnerships, research collaborations, clinical inquiries, and media - reach out and our team will respond.</p>
            <div className="mt-12 space-y-7">
              <div>
                <div className="eyebrow-dim mb-2">OFFICE</div>
                <div className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-[#FF6A4D] mt-1" />
                  <div className="text-[15px] text-white/85 leading-relaxed">Blude Life Science and Tech LLP<br />Ram Nagar, Keela Panangadi,<br />Madurai North, Madurai - 625 017.</div>
                </div>
              </div>
              <div>
                <div className="eyebrow-dim mb-2">PHONE</div>
                <div className="space-y-1.5">
                  {["+91 90879 71113", "+91 90879 71114", "+91 90879 71115"].map((p) => (
                    <a key={p} href={"tel:" + p.replace(/\s/g, "")} className="flex items-center gap-3 text-[15px] text-white/85 hover:text-[#FF6A4D] transition-colors">
                      <Phone className="w-4 h-4 text-[#FF6A4D]" />{p}
                    </a>
                  ))}
                </div>
              </div>
              <div>
                <div className="eyebrow-dim mb-2">EMAIL</div>
                <a href="mailto:contactus@blude.in" className="flex items-center gap-3 text-[15px] text-white/85 hover:text-[#FF6A4D] transition-colors">
                  <Mail className="w-4 h-4 text-[#FF6A4D]" />contactus@blude.in
                </a>
              </div>
            </div>
          </div>
          <motion.form onSubmit={submit} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.8 }} className="lg:col-span-7 glass-strong rounded-3xl p-7 sm:p-10 relative" data-testid="contact-form">
            <div className="absolute inset-0 rounded-3xl pointer-events-none border border-white/5" />
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
              <FloatingInput id="first_name" label="First Name *" value={form.first_name} onChange={update("first_name")} testid="contact-input-first-name" />
              <FloatingInput id="last_name" label="Last Name *" value={form.last_name} onChange={update("last_name")} testid="contact-input-last-name" />
              <FloatingInput id="email" type="email" label="Email *" value={form.email} onChange={update("email")} testid="contact-input-email" />
              <FloatingInput id="organisation" label="Organisation" value={form.organisation} onChange={update("organisation")} testid="contact-input-organisation" />
              <div className="relative sm:col-span-2 mt-2">
                <select id="area_of_interest" value={form.area_of_interest} onChange={update("area_of_interest")} className="w-full bg-transparent border-b border-white/15 px-1 pt-7 pb-2.5 text-white outline-none focus:border-[#FF2400] transition-colors appearance-none">
                  <option value="" className="bg-[#0A0A0C]">Select...</option>
                  {INTEREST_OPTIONS.map((o) => (<option key={o} value={o} className="bg-[#0A0A0C]">{o}</option>))}
                </select>
                <label className="absolute left-1 top-1 text-[10px] tracking-[0.25em] text-[#FF6A4D] uppercase pointer-events-none">Area of Interest</label>
              </div>
              <div className="sm:col-span-2 mt-2">
                <FloatingInput id="message" label="Message *" value={form.message} onChange={update("message")} testid="contact-input-message" />
              </div>
            </div>
            <div className="mt-9 flex flex-wrap items-center justify-between gap-4">
              <div className="text-[11px] font-mono tracking-widest text-white/40 max-w-sm">BY SUBMITTING YOU CONSENT TO BEING CONTACTED BY THE BLUDE TEAM.</div>
              <button type="submit" disabled={submitting || done} className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed">
                {done ? <><span>Submitted</span> <Check className="w-4 h-4" /></> : submitting ? <span>Sending...</span> : <><span>Send Message</span> <Send className="w-4 h-4" /></>}
              </button>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
};